import {
  ChevronDown,
  Info,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Sun,
  User,
  XIcon,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "src/components/common/Logo";
import Avatar from "src/components/ui/Avatar";
import Button from "src/components/ui/Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as authAdminActions from "src/redux/slices/authAdminSlice";
import * as authTenantActions from "src/redux/slices/authAdminSlice";
import { logoutAndRedirect } from "src/utils/auth";
import { useTheme } from "src/hooks/useTheme";
import MenuItems from "./Menu";
import NotificationBell from "./NotificationBell";
import AdminNotificationBell from "./AdminNotificationBell";
import TopbarSearch from "./TopbarSearch";

interface TopbarProps {
  type?: "admin" | "tenant";
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

// Which module the topbar's quick-add button targets, based on the current
// route — first matching prefix wins, falls back to "Add Product".
const QUICK_ADD_BY_MODULE: { prefix: string; label: string; path: string }[] = [
  { prefix: "/app/sale", label: "Add Sale", path: "/app/sale/create" },
  { prefix: "/app/purchase", label: "Add Purchase", path: "/app/purchase/create" },
];
const DEFAULT_QUICK_ADD = { label: "Add Product", path: "/app/product/add" };

const Topbar: React.FC<TopbarProps> = ({
  type = "tenant",
  collapsed = false,
  onToggleCollapsed,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const showCollapseToggle = type === "tenant";
  const quickAdd =
    QUICK_ADD_BY_MODULE.find((m) => location.pathname.startsWith(m.prefix)) ||
    DEFAULT_QUICK_ADD;

  return (
    <div className="bg-white border-b border-gray-300 dark:bg-gray-900 dark:border-gray-700">
      {/* Desktop View — split into two cells so the left one lines up exactly
          with the sidebar's width/border below it, and the right one lines
          up with the main content area. */}
      <div className="hidden md:flex items-stretch">
        <div
          className={`flex items-center border-r border-gray-300 dark:border-gray-700 shrink-0 py-4 transition-all duration-200 overflow-hidden ${
            showCollapseToggle && collapsed
              ? "w-16 justify-center"
              : "w-64 gap-3 px-4"
          }`}
        >
          {showCollapseToggle && collapsed ? (
            <button
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
              aria-label="Expand sidebar"
              onClick={onToggleCollapsed}
            >
              <PanelLeftOpen size={18} />
            </button>
          ) : (
            <>
              <Logo size="small" variant="contained" />
              <Logo size="large" variant="text" />
              {showCollapseToggle && (
                <button
                  className="ml-auto p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 shrink-0 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                  aria-label="Collapse sidebar"
                  onClick={onToggleCollapsed}
                >
                  <PanelLeftClose size={18} />
                </button>
              )}
            </>
          )}
        </div>

        <div className="flex-1 flex items-center justify-between gap-4 px-4 py-4 min-w-0">
          {type === "tenant" ? (
            <div className="flex-1 max-w-sm">
              <TopbarSearch />
            </div>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2 shrink-0">
            {type === "tenant" && (
              <Button
                size="sm"
                onClick={() => navigate(quickAdd.path)}
                className="hidden lg:inline-flex items-center gap-1.5"
              >
                <Plus size={15} />
                {quickAdd.label}
              </Button>
            )}

            <button
              className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
              aria-label="Toggle theme"
              onClick={toggleTheme}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {type === "tenant" && <NotificationBell variant="desktop" />}
            {type === "admin" && <AdminNotificationBell variant="desktop" />}

            <AvatarWrapper userType={type} />
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden p-4">
        <div className="flex items-center justify-between">
          <MenuWrapper type={type} />

          <Logo size="large" variant="text" />

          <div className="flex items-center gap-1">
            {type === "tenant" && <NotificationBell variant="mobile" />}
            {type === "admin" && <AdminNotificationBell variant="mobile" />}
            <AvatarWrapper userType={type} variant="mobile" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Topbar;

const AvatarWrapper: React.FC<{
  userType?: "admin" | "tenant";
  variant?: "mobile" | "desktop";
}> = ({ userType = "tenant", variant = "desktop" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const {
    user: { data: adminUser },
  } = useAppSelector((state) => state.authAdmin);
  const {
    user: { data: tenantUser },
  } = useAppSelector((state) => state.authTenant);

  const dispatch = useAppDispatch();

  const user = userType === "admin" ? adminUser : tenantUser;
  const fullName = user?.firstName + " " + user?.lastName;
  const firstName = fullName.split(" ")[0] || "";
  const clippedFirstName =
    firstName.length > 10 ? `${firstName.slice(0, 10)}…` : firstName;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    if (userType === "admin") {
      dispatch(authAdminActions.logout());
      logoutAndRedirect({ isAdmin: true, navigate });
    } else {
      dispatch(authTenantActions.logout());
      logoutAndRedirect({ navigate });
    }
  };

  const routePrefix = userType === "admin" ? "/admin" : "/app";

  if (variant === "mobile") {
    return (
      <div className="relative" ref={ref}>
        <button
          className="flex items-center space-x-2"
          onClick={() =>
            navigate(
              userType === "admin" ? "/admin/settings" : "/app/settings/profile"
            )
          }
        >
          <Avatar link={user?.avatar || ""} name={user?.firstName || ""} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex items-center space-x-2"
        onClick={() => setOpen((o) => !o)}
      >
        <Avatar link={user?.avatar || ""} name={user?.firstName || ""} />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {clippedFirstName}
        </span>
        <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-64 bg-white border rounded-xl shadow-lg p-3 z-50 dark:bg-gray-800 dark:border-gray-700">
          {/* User Info */}
          <div className="flex items-center space-x-3 p-2 border-b pb-3 dark:border-gray-700">
            <Avatar link={user?.avatar || ""} name={user?.firstName || ""} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="font-medium text-gray-800 dark:text-gray-100 truncate">
                  {clippedFirstName}
                </p>
                {user?.roleDetails && (
                  <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-500 dark:text-white">
                    {user.roleDetails.name}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>

          {/* Menu Items */}
          <div className="flex flex-col text-sm mt-2">
            <button
              className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded-md dark:text-gray-200 dark:hover:bg-gray-700/60"
              onClick={() =>
                navigate(
                  userType === "admin" ? "/admin/settings" : "/app/settings/profile"
                )
              }
            >
              <User size={16} className="text-gray-500 dark:text-gray-300" />
              <span className="font-medium">Profile</span>
            </button>
            {/* <button className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded-md">
                  <Settings size={16} className="text-gray-500" />
                  <span>Account settings</span>
                </button> */}
            <button
              className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded-md dark:text-gray-200 dark:hover:bg-gray-700/60"
              onClick={() => navigate(`${routePrefix}/support`)}
            >
              <Info size={16} className="text-gray-500 dark:text-gray-300" />
              <span>Support</span>
            </button>

            <div className="border-t my-2 dark:border-gray-700"></div>

            <button
              className="flex items-center space-x-2 text-red-600 hover:bg-red-50 p-2 rounded-md dark:hover:bg-red-900/30"
              onClick={handleLogout}
            >
              <LogOut size={16} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MenuWrapper: React.FC<{
  type: "admin" | "tenant";
}> = ({ type }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        variant="ghost"
        onClick={() => setOpen(!open)}
        //   onClick={toggleMobileMenu}
      >
        {open ? <XIcon size={24} /> : <Menu size={24} />}
      </Button>

      {open && (
        <div className="absolute -left-4 mt-3 w-screen bg-white border z-50 h-[calc(100vh-64px)] overflow-y-auto dark:bg-gray-900 dark:border-gray-700">
          <MenuItems type={type} />
        </div>
      )}
    </div>
  );
};
